import { useTranslation } from "next-i18next";
import { useState } from "react";
import {
  PromoAction,
  PromoActionType,
  PromoImageContent,
} from "../../../utils/promoMaterial/promoMaterialTypes";
import styles from "./PromoImageBanner.module.scss";

/**
 * The artwork's intrinsic box. Only the ratio matters — the CSS sizes the card —
 * but giving the element both stops the rail reflowing when each image lands.
 */
const IMAGE_WIDTH = 343;
const IMAGE_HEIGHT = 176;

/**
 * A campaign's artwork, the whole surface tappable — unlike the text cards,
 * there is nowhere on an image to put a button, and the artwork itself is the
 * call to action.
 *
 * A plain `<img>`, not `next/image`: this artwork is uploaded through the
 * loyalty admin, so its host is not in `next.config.js`'s `images.domains`
 * whitelist and the optimizer would reject it. That also lets a broken URL
 * remove the card entirely rather than leaving an empty frame in the rail.
 */
export const PromoImageBanner = ({
  content,
  action,
  onAction,
}: {
  content: PromoImageContent;
  action?: PromoAction;
  onAction: (action?: PromoAction) => void;
}) => {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const hasAction = action && action.type !== PromoActionType.NONE;

  if (failed) {
    return null;
  }

  /**
   * `alt=""` on purpose: the artwork is decorative *as an image* — an IMAGE
   * banner carries no copy at all on the wire (the loyalty side forbids it), so
   * there is nothing campaign-specific to describe. When the card is tappable
   * the accessible name belongs to the control instead, which is why the
   * interactive case is a real `<button>`: it gets Enter and Space, a focus ring
   * and a role for free, none of which a `<div onClick>` has.
   *
   * Dimensions are set so the artwork reserves its box before it loads — an
   * unsized image in the hero slot is measurable CLS.
   */
  const artwork = (
    <img
      className={styles.artwork}
      src={content.imageUrl}
      alt=""
      width={IMAGE_WIDTH}
      height={IMAGE_HEIGHT}
      onError={() => setFailed(true)}
    />
  );

  if (!hasAction) {
    return (
      <div
        className={`${styles.slide} rounded`}
        data-testid="promo_image_banner"
      >
        {artwork}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`${styles.slide} ${styles.tappable} ${styles.reset} rounded`}
      data-testid="promo_image_banner"
      aria-label={t("common:global-promotion")}
      onClick={() => onAction(action)}
    >
      {artwork}
    </button>
  );
};
